import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Receipt } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface JobCost {
  id: string;
  job_id: string;
  category: string;
  description: string;
  vendor: string | null;
  amount: number;
  cost_date: string;
  qb_synced: boolean;
}

interface Job { id: string; job_number: string; property_address: string; }

export function JobCostsManager({ orgId }: { orgId: string }) {
  const [costs, setCosts] = useState<JobCost[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    job_id: "", category: "materials", description: "", vendor: "",
    amount: "", cost_date: new Date().toISOString().split("T")[0],
  });

  useEffect(() => { load(); }, [orgId]);

  const load = async () => {
    setLoading(true);
    const [{ data: c }, { data: j }] = await Promise.all([
      supabase.from("job_costs").select("*").eq("org_id", orgId).order("cost_date", { ascending: false }).limit(100),
      supabase.from("jobs").select("id, job_number, property_address").eq("org_id", orgId).order("created_at", { ascending: false }).limit(200),
    ]);
    setCosts((c as any[]) || []);
    setJobs((j as any[]) || []);
    setLoading(false);
  };

  const save = async () => {
    if (!form.job_id || !form.description || !form.amount) {
      toast.error("Job, description and amount required");
      return;
    }
    const { error } = await supabase.from("job_costs").insert({
      org_id: orgId,
      job_id: form.job_id,
      category: form.category as any,
      description: form.description,
      vendor: form.vendor || null,
      amount: parseFloat(form.amount),
      cost_date: form.cost_date,
    });
    if (error) { toast.error(error.message); return; }
    toast.success("Cost added");
    setOpen(false);
    setForm({ job_id: "", category: "materials", description: "", vendor: "", amount: "", cost_date: new Date().toISOString().split("T")[0] });
    load();
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("job_costs").delete().eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success("Cost deleted");
    load();
  };

  const fmt = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">Job Costs Ledger</h3>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-2"><Plus className="w-4 h-4" />Add Cost</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Job Cost</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Job</Label>
                <Select value={form.job_id} onValueChange={(v) => setForm({ ...form, job_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select a job" /></SelectTrigger>
                  <SelectContent>
                    {jobs.map(j => (
                      <SelectItem key={j.id} value={j.id}>
                        {j.job_number || "—"} · {j.property_address}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["materials","labor","subcontractor","equipment","permits","dump_fees","other"].map(c => (
                      <SelectItem key={c} value={c}>{c.replace("_"," ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Vendor</Label>
                  <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
                </div>
                <div>
                  <Label>Amount</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.cost_date} onChange={(e) => setForm({ ...form, cost_date: e.target.value })} />
              </div>
              <Button onClick={save} className="w-full">Save Cost</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
      ) : costs.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">No costs logged yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {costs.map(c => (
              <TableRow key={c.id}>
                <TableCell>{format(new Date(c.cost_date), "MMM d, yyyy")}</TableCell>
                <TableCell className="capitalize">{c.category.replace("_"," ")}</TableCell>
                <TableCell>{c.description}</TableCell>
                <TableCell className="text-muted-foreground">{c.vendor || "—"}</TableCell>
                <TableCell className="text-right font-medium">{fmt(Number(c.amount))}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => remove(c.id)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
